import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface RequestParameter {
  id: string;
  name: string;
  method: string;
  type: string;
  value: string;
  endpoint: string;
  timestamp: string;
  source?: string;
  userAgent?: string;
  website: string;
  scanId: number;
  resourceType?: string;
  sensitive: boolean;
}

interface RequestParametersResponse {
  parameters: RequestParameter[];
  summary: {
    totalParameters: number;
    getParameters: number;
    postParameters: number;
    sensitiveParameters: number;
    headerParameters: number;
    bodyParameters: number;
    queryParameters: number;
  };
  lastUpdated: string;
}
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Settings, 
  Code, 
  Globe, 
  Search,
  Filter,
  Download,
  Activity,
  Database,
  CheckCircle,
  AlertTriangle,
  Copy,
  Eye,
  EyeOff
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function RequestParameters() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMethod, setFilterMethod] = useState("all");
  const [showSensitive, setShowSensitive] = useState(false);
  const [activeCardFilter, setActiveCardFilter] = useState<string | null>(null); // 'total' | 'get' | 'post' | 'sensitive' | null
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: parametersData, isLoading, error, refetch } = useQuery<RequestParametersResponse>({
    queryKey: ["/api/request-parameters"],
    refetchInterval: 5000, // More aggressive refresh 
    staleTime: 0, // Always treat as stale
    cacheTime: 0, // Don't cache
    refetchOnMount: "always", // Always refetch on mount
    refetchOnReconnect: true, // Refetch on reconnect
    refetchOnWindowFocus: true, // Refetch on window focus
  });

  // Force invalidate and refetch on component mount
  useEffect(() => {
    // Clear all cache and force fresh fetch
    queryClient.removeQueries({ queryKey: ["/api/request-parameters"] });
    queryClient.invalidateQueries({ queryKey: ["/api/request-parameters"] });
    setTimeout(() => {
      refetch();
    }, 100);
  }, [queryClient, refetch]);

  const parameters = parametersData?.parameters || [];
  const summary = parametersData?.summary || {
    totalParameters: 0,
    getParameters: 0,
    postParameters: 0,
    sensitiveParameters: 0
  };

  // Debug logging - remove after testing
  console.log('Request Parameters Data:', {
    parametersData,
    parametersLength: parameters.length,
    summary,
    isLoading,
    error,
    rawAPIData: parametersData // Show complete API response
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case "GET": return "bg-green-100 text-green-800 border-green-200";
      case "POST": return "bg-blue-100 text-blue-800 border-blue-200";
      case "PUT": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "DELETE": return "bg-red-100 text-red-800 border-red-200";
      case "PATCH": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getParameterTypeColor = (type: string) => {
    switch (type) {
      case "query": return "bg-blue-100 text-blue-800 border-blue-200";
      case "body": return "bg-green-100 text-green-800 border-green-200";
      case "header": return "bg-purple-100 text-purple-800 border-purple-200";
      case "path": return "bg-orange-100 text-orange-800 border-orange-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  // Helper function to check if a parameter is sensitive (must be declared before use)
  const isSensitiveParameter = (name: string, value: string) => {
    const sensitiveKeywords = ['password', 'token', 'key', 'secret', 'auth', 'credential', 'session', 'csrf', 'jwt', 'api_key', 'apikey'];
    const paramName = name.toLowerCase();
    return sensitiveKeywords.some(keyword => paramName.includes(keyword)) ||
           (value && value.length > 20 && /^[A-Za-z0-9+/=]{20,}$/.test(value));
  };

  const handleCardClick = (filterType: 'total' | 'get' | 'post' | 'sensitive') => {
    if (activeCardFilter === filterType) {
      // Clicking the same card again clears the filter
      setActiveCardFilter(null);
    } else {
      setActiveCardFilter(filterType);
    }
    // Also update method filter if clicking get/post
    if (filterType === 'get') {
      setFilterMethod('get');
    } else if (filterType === 'post') {
      setFilterMethod('post');
    } else if (filterType === 'total') {
      setFilterMethod('all');
    }
  };

  const filteredParameters = parameters.filter((param: any) => {
    const matchesSearch = 
      param.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      param.endpoint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      param.value?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterMethod === "all" || param.method.toLowerCase() === filterMethod.toLowerCase();
    
    // Apply card filter
    let matchesCardFilter = true;
    if (activeCardFilter === 'get') {
      matchesCardFilter = param.method.toLowerCase() === 'get';
    } else if (activeCardFilter === 'post') {
      matchesCardFilter = param.method.toLowerCase() === 'post';
    } else if (activeCardFilter === 'sensitive') {
      matchesCardFilter = param.sensitive === true || isSensitiveParameter(param.name, param.value);
    }
    // 'total' or null means show all
    
    return matchesSearch && matchesFilter && matchesCardFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Request Parameters
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Monitor and analyze all API request parameters and data flow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/request-parameters';
              link.download = `request-parameters-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Parameters
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSensitive(!showSensitive)}
          >
            {showSensitive ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showSensitive ? "Hide" : "Show"} Sensitive
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Activity className="h-3 w-3 mr-1" />
            {summary.totalParameters} Parameters
          </Badge>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'total' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950' : ''
          }`}
          onClick={() => handleCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'total' ? 'bg-blue-200' : 'bg-blue-100'
              }`}>
                <Settings className={`h-5 w-5 ${
                  activeCardFilter === 'total' ? 'text-blue-700' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.totalParameters}
                </div>
                <div className="text-xs text-slate-600">Total Parameters</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'total' ? 'text-blue-700 font-semibold' : 'text-blue-600'
                }`}>
                  <Database className="w-3 h-3 inline mr-1" />
                  All requests
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'get' ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-950' : ''
          }`}
          onClick={() => handleCardClick('get')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'get' ? 'bg-green-200' : 'bg-green-100'
              }`}>
                <Globe className={`h-5 w-5 ${
                  activeCardFilter === 'get' ? 'text-green-700' : 'text-purple-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.getParameters}
                </div>
                <div className="text-xs text-slate-600">GET Parameters</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'get' ? 'text-green-700 font-semibold' : 'text-purple-600'
                }`}>
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  Query strings
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeCardFilter === 'post' ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-950' : ''
          }`}
          onClick={() => handleCardClick('post')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'post' ? 'bg-purple-200' : 'bg-purple-100'
              }`}>
                <Code className={`h-5 w-5 ${
                  activeCardFilter === 'post' ? 'text-purple-700' : 'text-purple-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.postParameters}
                </div>
                <div className="text-xs text-slate-600">POST Parameters</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'post' ? 'text-purple-700 font-semibold' : 'text-purple-600'
                }`}>
                  Request bodies
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md border-2 ${
            activeCardFilter === 'sensitive'
              ? 'ring-2 ring-red-500 border-red-400 bg-red-50 dark:bg-red-950 dark:border-red-600'
              : 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30'
          }`}
          onClick={() => handleCardClick('sensitive')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeCardFilter === 'sensitive' ? 'bg-red-200' : 'bg-red-100'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  activeCardFilter === 'sensitive' ? 'text-red-700' : 'text-red-600'
                }`} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.sensitiveParameters}
                </div>
                <div className="text-xs text-slate-600">Sensitive Data</div>
                <div className={`text-xs mt-1 ${
                  activeCardFilter === 'sensitive' ? 'text-red-700 font-semibold' : 'text-red-600'
                }`}>
                  Requires attention
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by parameter name, endpoint, or value..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Methods</option>
            <option value="get">GET</option>
            <option value="post">POST</option>
            <option value="put">PUT</option>
            <option value="delete">DELETE</option>
            <option value="patch">PATCH</option>
          </select>
        </div>
      </div>

      {/* Parameters List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-blue-600" />
            Request Parameters ({filteredParameters.length})
          </CardTitle>
          <CardDescription>
            Complete list of all request parameters captured from API calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading parameters...</span>
            </div>
          ) : filteredParameters.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Settings className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Parameters Found</div>
              <div className="text-sm space-y-2">
                {searchTerm || filterMethod !== "all" ? (
                  <div>Try adjusting your search or filter criteria.</div>
                ) : (
                  <>
                    <div>No request parameters have been captured yet.</div>
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="text-blue-700 dark:text-blue-300 text-sm space-y-1">
                        <div className="font-medium">💡 Request parameters are captured during domain scans</div>
                        <div>• Go to Domain Scanner and scan any website</div>
                        <div>• Parameters are extracted from forms, JavaScript, APIs, and tracking scripts</div>
                        <div>• Results will appear here automatically after scanning</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredParameters.map((param: any) => {
                const isSensitive = param.sensitive === true || isSensitiveParameter(param.name, param.value);
                return (
                  <div
                    key={param.id}
                    className={`flex items-start justify-between p-4 border-2 rounded-lg transition-colors ${
                      isSensitive
                        ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50'
                        : 'border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs border ${getMethodColor(param.method)}`}>
                            {param.method}
                          </Badge>
                          <Badge className={`text-xs border ${getParameterTypeColor(param.type)}`}>
                            {param.type}
                          </Badge>
                          {isSensitive && (
                            <Badge className="text-xs border bg-red-100 text-red-800 border-red-200">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Sensitive
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-sm">{param.name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(param.name)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="text-xs text-slate-600 mb-2">
                          <span className="font-medium">Endpoint:</span> {param.endpoint}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-slate-500">Value:</span>
                          <span className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded max-w-md truncate">
                            {isSensitive && !showSensitive
                              ? "••••••••••••••••"
                              : param.value || "No value"
                            }
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(param.value || "")}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Timestamp: {new Date(param.timestamp || Date.now()).toLocaleString()}</span>
                          <span>Source: {param.source || "Unknown"}</span>
                          {param.userAgent && (
                            <span>User Agent: {param.userAgent}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}